/**
 * errorReporter — the single shared, privacy-safe, fail-safe client-error /
 * telemetry sink for the student SPA.
 *
 * BUG-20260722-000251 (T-1, RC-002 observability): before this module, every
 * frontend error path terminated at a device-local console.* call with zero
 * off-device signal (see the bug's RCA). This module is the ONE place every
 * in-scope capture site (React 19 root hooks, the ErrorBoundary, global
 * window listeners, and the two highest-value existing catch sites) funnels
 * through, so the privacy allowlist and the error/event metric contract are
 * enforced exactly once (2026-07-23).
 *
 * Two entry points, ONE discriminator (ISS-003 exact metric contract):
 *   - reportClientError(...) ALWAYS sets type: 'error' — METRIC-ELIGIBLE. The
 *     backend ingest (backend/src/handlers/client_error.py) increments the
 *     ThinkOtter/Client ClientError CloudWatch metric for these.
 *   - reportClientEvent(...) ALWAYS sets type: 'event' — LOG-ONLY. Written to
 *     the durable log sink but never increments the metric (e.g. a boundary
 *     catch's componentStack, or promo_gate_rejection telemetry).
 * This split is what keeps a single boundary-caught render error from
 * double-counting the ClientError metric: componentDidCatch reports an
 * 'event' (ErrorBoundary.tsx, T-2); the root onCaughtError hook reports the
 * ONE metric-eligible 'error' (main.tsx, T-4).
 *
 * PRIVACY (hard constraint): the payload is a strict allowlist. It NEVER
 * contains a resolved pathname/sessionId, answer/passage text, userId,
 * Cognito sub, tokens, or cookies — only error name/message/componentStack
 * (length-capped), a route PATTERN (never a resolved id — see
 * routePatternFromPath), and coarse client metadata (userAgent, viewport,
 * buildId, timestamp). Server-side (client_error.py) re-applies the same
 * allowlist as defense in depth.
 *
 * FAIL-SAFE (TO-LL-036): every exported function wraps its entire body in a
 * try/catch that never re-throws, following the established
 * gateTelemetry.ts:38-45 pattern — a reporter that itself throws would crash
 * exactly when it is needed most (e.g. inside a component already failing).
 *
 * Transport: navigator.sendBeacon with a 'text/plain' Blob body (CORS-simple,
 * so no preflight; survives an immediate reload/unload; needs no auth token,
 * so it also covers pre-auth routes like /login and /auth/callback).
 * sendBeacon is undefined in jsdom, so this module is a silent no-op across
 * the existing frontend test suite (no MSW/network coupling introduced).
 */

import { matchPath } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { getBuildId } from '@/lib/buildId';

/** Discriminator: 'error' increments the backend ClientError metric; 'event' does not. */
type ReportType = 'error' | 'event';

/** The complete, privacy-safe wire payload. Every field here is allowlisted. */
interface ReportPayload {
  type: ReportType;
  /** Capture-site identifier, e.g. 'react_uncaught', 'answer_submit', 'promo_gate_rejection'. */
  source: string;
  /** Error name (e.g. 'TypeError'), when applicable. */
  name?: string;
  /** Error message / advisory summary, truncated. Never interpolates user content. */
  message?: string;
  /** React componentStack, truncated. */
  componentStack?: string;
  /** A registered route PATTERN (e.g. '/session/:sessionId') — never a resolved id. */
  routePattern: string;
  userAgent: string;
  viewport: { w: number; h: number };
  buildId: string;
  ts: number;
}

const MAX_MESSAGE_LENGTH = 1000;
const MAX_STACK_LENGTH = 4000;
const MAX_USER_AGENT_LENGTH = 400;
const MAX_SENDS_PER_LOAD = 10;
const DEDUP_WINDOW_MS = 5000;

// The exact wire-allowlist. dispatch() is the single enforcement point so
// neither reportClientError nor reportClientEvent can smuggle an extra key
// onto the wire regardless of what a call site passes in.
const ALLOWED_KEYS = [
  'type',
  'source',
  'name',
  'message',
  'componentStack',
  'routePattern',
  'userAgent',
  'viewport',
  'buildId',
  'ts',
] as const satisfies readonly (keyof ReportPayload)[];

// The COMPLETE registered route-pattern set (ISS-001): Object.values(ROUTES),
// extended by T-12 to include the student/alias patterns that used to be
// inline App.tsx literals (e.g. '/session/:sessionId'). '*' is never a ROUTES
// value, but the exclusion is kept explicit per the fix plan. Widened to
// string[] first so the '*' exclusion type-checks regardless of ROUTES'
// literal-union value type.
const ALL_ROUTE_VALUES: string[] = Object.values(ROUTES);
const REGISTERED_ROUTE_PATTERNS: string[] = ALL_ROUTE_VALUES.filter((pattern) => pattern !== '*');

/**
 * Derive a registered route PATTERN from a resolved pathname, e.g.
 * '/session/abc123' -> '/session/:sessionId'. Never returns a resolved id;
 * falls back to 'unknown' for a genuinely unmatched path (never leaks the
 * literal pathname).
 */
export function routePatternFromPath(pathname: string): string {
  for (const pattern of REGISTERED_ROUTE_PATTERNS) {
    if (matchPath(pattern, pathname)) {
      return pattern;
    }
  }
  return 'unknown';
}

function getCurrentPathname(): string {
  try {
    return typeof window !== 'undefined' && window.location ? window.location.pathname : '';
  } catch {
    return '';
  }
}

/** Coarse, privacy-safe client metadata shared by every report. */
function buildEnvelopeMeta(): Pick<ReportPayload, 'userAgent' | 'viewport' | 'buildId' | 'ts'> {
  const userAgent =
    typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
      ? navigator.userAgent.slice(0, MAX_USER_AGENT_LENGTH)
      : 'unknown';
  const viewport = {
    w: typeof window !== 'undefined' ? window.innerWidth : 0,
    h: typeof window !== 'undefined' ? window.innerHeight : 0,
  };
  // feedback-implementation/TS-003 (T3, AC-TS003.5, TO-LL-036): routed
  // through getBuildId() rather than a bare `?? 'unknown'` — `??` does not
  // coalesce an empty string, and CI now defines VITE_APP_BUILD_ID (T1), so
  // an empty/whitespace value is newly reachable. See frontend/src/lib/buildId.ts.
  const buildId = getBuildId();
  return { userAgent, viewport, buildId, ts: Date.now() };
}

/** Narrow an unknown thrown value to a safe { name?, message? } shape, never throwing. */
function toErrorLike(error: unknown): { name?: string; message?: string } {
  if (error instanceof Error) {
    return { name: error.name, message: error.message };
  }
  if (typeof error === 'object' && error !== null) {
    const maybe = error as { name?: unknown; message?: unknown };
    return {
      name: typeof maybe.name === 'string' ? maybe.name : undefined,
      message: typeof maybe.message === 'string' ? maybe.message : undefined,
    };
  }
  return { message: typeof error === 'string' ? error : undefined };
}

function toAllowlistedPayload(payload: ReportPayload): Partial<ReportPayload> {
  const out: Partial<ReportPayload> = {};
  for (const key of ALLOWED_KEYS) {
    const value = payload[key];
    if (value !== undefined) {
      (out as Record<string, unknown>)[key] = value;
    }
  }
  return out;
}

// Module-scoped, never persisted: dedup suppresses an identical
// type|source|name|message|routePattern seen again within 5s; the hard cap
// bounds total sends to 10 per page load. Both are abuse/noise controls, not
// privacy controls (the allowlist is the privacy control).
const dedupCache = new Map<string, number>();
let sendCount = 0;

function dedupKey(payload: ReportPayload): string {
  return [
    payload.type,
    payload.source,
    payload.name ?? '',
    payload.message ?? '',
    payload.routePattern,
  ].join('|');
}

function shouldSuppress(payload: ReportPayload): boolean {
  const key = dedupKey(payload);
  const last = dedupCache.get(key);
  if (last !== undefined && payload.ts - last < DEDUP_WINDOW_MS) {
    return true;
  }
  dedupCache.set(key, payload.ts);
  return false;
}

/**
 * Single private dispatch point: enforces the allowlist, dedup, the 10/load
 * cap, and the sendBeacon transport. Never throws (TO-LL-036) — a failure
 * here must never break the caller (which may itself be a crashing tree).
 */
function dispatch(payload: ReportPayload): void {
  try {
    if (import.meta.env.DEV) {
      // Local-devtools visibility only; never the sole record (TO-LL-036 exception form).
      console.error('[errorReporter]', payload);
    }

    if (shouldSuppress(payload)) {
      return;
    }
    if (sendCount >= MAX_SENDS_PER_LOAD) {
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function' || !apiUrl) {
      // No transport available (also true for the entire jsdom test suite) — silent no-op.
      return;
    }

    const scrubbed = toAllowlistedPayload(payload);
    const blob = new Blob([JSON.stringify(scrubbed)], { type: 'text/plain' });
    navigator.sendBeacon(`${apiUrl}/client-errors`, blob);
    sendCount += 1;
  } catch {
    // Never let a reporting failure propagate — this sink must be fail-safe.
  }
}

/** Input for reportClientError — the METRIC-ELIGIBLE ('error') report path. */
export interface ReportClientErrorInput {
  name?: string;
  message?: string;
  componentStack?: string;
  /** Capture-site identifier, e.g. 'react_uncaught', 'answer_submit'. */
  source: string;
}

/**
 * Report a client error. ALWAYS type: 'error' — the backend ingest
 * increments the ThinkOtter/Client ClientError metric for these (and only
 * these). Never throws.
 */
export function reportClientError(raw: ReportClientErrorInput): void {
  try {
    dispatch({
      type: 'error',
      source: raw.source,
      name: raw.name,
      message: raw.message !== undefined ? raw.message.slice(0, MAX_MESSAGE_LENGTH) : undefined,
      componentStack:
        raw.componentStack !== undefined
          ? raw.componentStack.slice(0, MAX_STACK_LENGTH)
          : undefined,
      routePattern: routePatternFromPath(getCurrentPathname()),
      ...buildEnvelopeMeta(),
    });
  } catch {
    // Never throw into the render/caller path.
  }
}

/**
 * Report an advisory, LOG-ONLY event. ALWAYS type: 'event' — never
 * increments the ClientError metric, regardless of what `data` carries.
 * `name` is the event identifier (e.g. 'promo_gate_rejection',
 * 'react_boundary_caught'). `data` may ONLY populate the same allowlisted
 * name/message/componentStack/routePattern fields the wire payload already
 * has — every OTHER key is deliberately IGNORED (never folded into message,
 * never smuggled onto the wire as a new key), so a careless or hostile call
 * site can never widen the allowlist by passing extra data. Never throws.
 */
export function reportClientEvent(name: string, data: Record<string, unknown> = {}): void {
  try {
    const dataName = data.name;
    const dataMessage = data.message;
    const dataStack = data.componentStack;
    const dataRoutePattern = data.routePattern;

    dispatch({
      type: 'event',
      source: name,
      name: typeof dataName === 'string' ? dataName : undefined,
      message:
        typeof dataMessage === 'string' ? dataMessage.slice(0, MAX_MESSAGE_LENGTH) : undefined,
      componentStack:
        typeof dataStack === 'string' ? dataStack.slice(0, MAX_STACK_LENGTH) : undefined,
      routePattern:
        typeof dataRoutePattern === 'string'
          ? dataRoutePattern
          : routePatternFromPath(getCurrentPathname()),
      ...buildEnvelopeMeta(),
    });
  } catch {
    // Never throw into the render/caller path.
  }
}

/** Minimal shape shared by React 19's root-hook and componentDidCatch error-info objects. */
interface MinimalErrorInfo {
  componentStack?: string;
}

/**
 * React 19 createRoot root hook: an error that escaped every boundary
 * (nothing above it to contain it). METRIC-ELIGIBLE (type 'error').
 */
export function handleReactUncaughtError(error: unknown, errorInfo: MinimalErrorInfo): void {
  const errLike = toErrorLike(error);
  reportClientError({
    name: errLike.name,
    message: errLike.message ?? String(error),
    componentStack: errorInfo?.componentStack,
    source: 'react_uncaught',
  });
}

/**
 * React 19 createRoot root hook: an error a boundary DID catch. This is the
 * SINGLE metric-eligible path for a boundary-caught render error (ISS-003) —
 * ErrorBoundary.componentDidCatch reports the same error as a log-only
 * 'event' instead, so the ClientError metric increments exactly once.
 */
export function handleReactCaughtError(error: unknown, errorInfo: MinimalErrorInfo): void {
  const errLike = toErrorLike(error);
  reportClientError({
    name: errLike.name,
    message: errLike.message ?? String(error),
    componentStack: errorInfo?.componentStack,
    source: 'react_caught',
  });
}

/**
 * React 19 createRoot root hook: a recoverable error (React retried and
 * succeeded). LOG-ONLY (type 'event') — the app did not actually fail.
 */
export function handleReactRecoverableError(error: unknown, errorInfo: MinimalErrorInfo): void {
  const errLike = toErrorLike(error);
  reportClientEvent('react_recoverable', {
    name: errLike.name,
    message: errLike.message ?? String(error),
    componentStack: errorInfo?.componentStack,
  });
}

let globalHandlersInstalled = false;

/**
 * Install window 'error' + 'unhandledrejection' listeners once (RC-005): the
 * class of failure a React error boundary structurally cannot catch (event
 * handlers, timers, truly uncaught async code). METRIC-ELIGIBLE (type
 * 'error'). Idempotent — safe to call more than once (e.g. under
 * <StrictMode> double-invocation of module-scope init in dev).
 */
export function installGlobalErrorHandlers(): void {
  if (globalHandlersInstalled) {
    return;
  }
  try {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('error', (event: ErrorEvent) => {
      const errLike = toErrorLike(event?.error);
      reportClientError({
        name: errLike.name ?? 'Error',
        message: errLike.message ?? event?.message,
        source: 'window_error',
      });
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      const errLike = toErrorLike(event?.reason);
      reportClientError({
        name: errLike.name ?? 'UnhandledRejection',
        message: errLike.message ?? String(event?.reason),
        source: 'unhandled_rejection',
      });
    });

    globalHandlersInstalled = true;
  } catch {
    // Never throw during app init.
  }
}
